import { Seo, Reveal } from "../components/Seo";
import { PageHero, SectionHead, CtaBand } from "../components/blocks";
import { IMG } from "../lib/site";

const PRINCIPLES = [
  {
    n: "01",
    t: "Secure system design",
    d: "Threat-informed architecture, least-privilege access, encryption in transit and at rest, and security review as a standard gate in delivery — not a final checklist.",
  },
  {
    n: "02",
    t: "Privacy and data protection",
    d: "Processing aligned to the Nigeria Data Protection Act 2023 and applicable guidance: data minimisation, defined retention, consent where required, and processor obligations honoured.",
  },
  {
    n: "03",
    t: "Enterprise architecture",
    d: "Documented target states, architecture decision records and integration standards, so systems fit together today and remain changeable tomorrow.",
  },
  {
    n: "04",
    t: "Quality assurance",
    d: "Automated testing, staged delivery, code review and release discipline across every product and engagement.",
  },
  {
    n: "05",
    t: "Integration governance",
    d: "Versioned interfaces, monitoring, failure-handling design and operational documentation for every integration we build or join.",
  },
  {
    n: "06",
    t: "Operational reliability",
    d: "Observability, runbooks, backup and recovery procedures — agreed before go-live, reviewed after it.",
  },
];

export default function TechnologySecurity() {
  return (
    <>
      <Seo
        title="Technology & Security — Softline Nigeria"
        description="How we engineer: secure system design, privacy and data protection, enterprise architecture, quality assurance, integration governance and operational reliability."
        path="/technology-security"
      />
      <PageHero
        eyebrow="Technology & security"
        title={["Built on sound", "engineering principles."]}
        lead="The working methods behind every Softline product and engagement — stated as practices, not as certifications we have not earned."
        crumbs={[{ label: "Home", to: "/" }, { label: "Technology & Security" }]}
      />

      <section className="bg-white" data-testid="tech-principles">
        <div className="shell py-24 md:py-32">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-charcoal/10 border border-charcoal/10">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.05} className="h-full">
                <div className="h-full bg-white p-8" data-testid={`tech-principle-${p.n}`}>
                  <span className="font-mono text-sm text-charcoal/40">{p.n}</span>
                  <h2 className="font-display font-bold text-xl mt-4">{p.t}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/65">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-charcoal noise-bg" data-testid="tech-separation">
        <div className="shell py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <SectionHead
              dark
              eyebrow="Corporate and product separation"
              title="This website never touches product infrastructure."
              lead="Softline's corporate web presence is deliberately separated from the systems that run our products. Customer authentication, identity credentials, financial workflows and sensitive records live in independently secured application environments — never inside this website."
            />
            <Reveal delay={0.2}>
              <ul className="mt-9 space-y-3">
                {[
                  "No product credentials or secrets are held in this website's environment.",
                  "Product links point to independently secured application environments.",
                  "Demonstrations use isolated environments and synthetic data.",
                  "Compliance claims are published only with verifiable evidence.",
                ].map((li) => (
                  <li key={li} className="flex items-start gap-3 text-[15px] text-softgrey">
                    <span className="h-2 w-2 bg-lime mt-2 shrink-0" aria-hidden="true"></span>
                    {li}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <figure className="border border-white/15 bg-ink/60 p-2">
                <img
                  src={IMG.identity}
                  alt="Digital identity and security technology — illustrative photography"
                  className="aspect-[4/5] w-full object-cover"
                  loading="lazy"
                />
                <figcaption className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-softgrey/70">
                  Illustrative photography
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
